import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Upload, X, Calendar, MapPin, AlertCircle, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** /events/new — Submit a vending or community event.
 *  Mandatory: title, date, city, flyer.  */
export default function NewEventPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState({
    title: '', date_iso: '', end_date_iso: '',
    venue_name: '', address: '', city: '', state: '', country: 'US',
    description: '',
    open_to_vendors: true, vendor_fee: '', vendor_slots: '', consumer_ticket_price: '',
    signup_url: '', consumer_signup_url: '', contact_email: '', contact_phone: '', organizer_website: '',
    tags: '',
  });
  const [flyer, setFlyer] = useState(null);  // {url, filename}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);  // existing event when dup detected
  const flyerInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/auth?next=/events/new');
  }, [user, authLoading, navigate]);

  // Pre-flight duplicate check whenever title+date change (debounced)
  useEffect(() => {
    if (!form.title || !form.date_iso || form.title.trim().length < 3) { setDupWarning(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/events/check-duplicate?title=${encodeURIComponent(form.title)}&date_iso=${encodeURIComponent(form.date_iso)}`);
        const data = await res.json();
        if (data.is_duplicate) setDupWarning(data.event);
        else setDupWarning(null);
      } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [form.title, form.date_iso]);

  const onFlyer = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error('File too large (50MB max)');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/media/upload?category=event-flyer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Upload failed'); return; }
      const url = data.url?.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setFlyer({ url, filename: file.name });
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) return toast.error('Event name is too short');
    if (!form.date_iso) return toast.error('Date is required');
    if (!form.city.trim()) return toast.error('City is required');
    if (!flyer) return toast.error('Flyer image is required');
    if (dupWarning) return toast.error('An event with this name on this date already exists');

    setSubmitting(true);
    const tagsArr = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
    const body = {
      title: form.title.trim(),
      date_iso: form.date_iso,
      end_date_iso: form.end_date_iso || null,
      location: {
        venue_name: form.venue_name || null,
        address: form.address || null,
        city: form.city,
        state: form.state || null,
        country: form.country || 'US',
      },
      flyer_url: flyer.url,
      description: form.description,
      open_to_vendors: !!form.open_to_vendors,
      vendor_fee: form.vendor_fee ? Number(form.vendor_fee) : null,
      vendor_slots: form.vendor_slots ? Number(form.vendor_slots) : null,
      consumer_ticket_price: form.consumer_ticket_price ? Number(form.consumer_ticket_price) : null,
      signup_url: form.signup_url || null,
      consumer_signup_url: form.consumer_signup_url || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
      organizer_website: form.organizer_website || null,
      tags: tagsArr,
    };
    try {
      const res = await fetch(`${API}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        const detail = data.detail || {};
        toast.error(detail.message || 'Duplicate event detected');
        if (detail.existing_event_id) navigate(`/events/${detail.existing_event_id}`);
        setSubmitting(false);
        return;
      }
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not post event'); setSubmitting(false); return; }
      toast.success('Event posted!');
      navigate(`/events/${data.event.event_id}`);
    } catch { toast.error('Network error'); setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-12" data-testid="event-new-page">
        <div className="mb-8">
          <p className="text-xs text-fuchsia-300 font-bold tracking-widest uppercase mb-2">Post an Event</p>
          <h1 className="text-3xl sm:text-4xl font-bold font-syne leading-tight">
            Post a vending event<br />
            <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
              and let the community find it.
            </span>
          </h1>
          <p className="text-zinc-400 text-sm mt-3">
            Pop-up markets, festivals, conferences. Required: name, date, location, flyer.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {/* Flyer upload (mandatory) */}
          <div>
            <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-300 mb-1.5 flex items-center gap-1">
              <ImageIcon size={11} className="text-fuchsia-300" /> Flyer image <span className="text-fuchsia-400">*</span>
            </span>
            {flyer ? (
              <div className="sticky top-20 z-10">
                <div className="relative rounded-xl bg-black aspect-[4/5] overflow-hidden max-h-[55vh] sm:max-h-[70vh]">
                  <img src={flyer.url} alt="flyer" className="w-full h-full object-contain" />
                  <button type="button" onClick={() => setFlyer(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-red-500/80 text-white flex items-center justify-center" data-testid="event-flyer-remove">
                    <X size={14} />
                  </button>
                  <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold backdrop-blur-sm">Preview stays in view while you scroll</span>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => flyerInputRef.current?.click()}
                disabled={uploading}
                className="w-full aspect-[4/5] sm:aspect-[16/9] rounded-xl border-2 border-dashed border-fuchsia-500/30 hover:border-fuchsia-400 bg-white/5 flex flex-col items-center justify-center gap-2 text-zinc-300 hover:text-white"
                data-testid="event-flyer-pick"
              >
                {uploading ? <Loader2 size={28} className="animate-spin text-fuchsia-300" /> : <Upload size={28} className="text-fuchsia-300" />}
                <span className="text-sm font-semibold">{uploading ? 'Uploading…' : 'Upload event flyer'}</span>
                <span className="text-[11px] text-zinc-500">JPG · PNG · WEBP · HEIC · 50MB max</span>
              </button>
            )}
            <input ref={flyerInputRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { onFlyer(e.target.files?.[0]); e.target.value = ''; }} />
          </div>

          {/* Title (mandatory) */}
          <Field label="Event name" required>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} placeholder="e.g. Indigenous Pop-Up Market" className="form-input" data-testid="event-title" />
          </Field>

          {/* Duplicate warning banner */}
          {dupWarning && (
            <div className="rounded-2xl bg-rose-500/10 border border-rose-500/30 p-3 flex items-start gap-3" data-testid="event-dup-warning">
              <AlertCircle size={16} className="text-rose-300 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-white font-semibold">An event with this name on this date already exists.</p>
                <p className="text-zinc-300 text-xs mt-1">"{dupWarning.title}" was posted by {dupWarning.host?.name}.</p>
                <button type="button" onClick={() => navigate(`/events/${dupWarning.event_id}`)} className="mt-2 text-fuchsia-300 hover:text-fuchsia-200 text-xs font-bold underline">View existing event →</button>
              </div>
            </div>
          )}

          {/* Date(s) — mandatory start */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" icon={Calendar} required>
              <input type="date" value={form.date_iso} onChange={(e) => setForm({ ...form, date_iso: e.target.value })} className="form-input" data-testid="event-date" />
            </Field>
            <Field label="End date (optional)" icon={Calendar}>
              <input type="date" value={form.end_date_iso} onChange={(e) => setForm({ ...form, end_date_iso: e.target.value })} className="form-input" data-testid="event-end-date" />
            </Field>
          </div>

          {/* Location — city is mandatory */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Venue name (optional)">
              <input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} placeholder="e.g. Civic Plaza" className="form-input" data-testid="event-venue" />
            </Field>
            <Field label="Address (optional)" icon={MapPin}>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street address" className="form-input" data-testid="event-address" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="City" required icon={MapPin}>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Albuquerque" className="form-input" data-testid="event-city" />
            </Field>
            <Field label="State / Province">
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="NM" className="form-input" data-testid="event-state" />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="US" className="form-input" data-testid="event-country" />
            </Field>
          </div>

          {/* Description */}
          <Field label="Description" hint="What can vendors expect? Foot traffic, vibe, audience.">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} maxLength={5000} className="form-input resize-none" data-testid="event-description" />
          </Field>

          {/* Vendor section */}
          <div className="rounded-2xl bg-fuchsia-500/5 border border-fuchsia-500/20 p-4 space-y-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.open_to_vendors} onChange={(e) => setForm({ ...form, open_to_vendors: e.target.checked })} className="w-4 h-4" data-testid="event-open-vendors" />
              <span className="text-white font-semibold text-sm">Open to vendors (vending opportunity)</span>
            </label>
            {form.open_to_vendors && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Booth fee ($)">
                    <input type="number" min="0" value={form.vendor_fee} onChange={(e) => setForm({ ...form, vendor_fee: e.target.value })} placeholder="50" className="form-input" data-testid="event-vendor-fee" />
                  </Field>
                  <Field label="Vendor slots">
                    <input type="number" min="1" value={form.vendor_slots} onChange={(e) => setForm({ ...form, vendor_slots: e.target.value })} placeholder="25" className="form-input" data-testid="event-vendor-slots" />
                  </Field>
                  <Field label="Ticket price ($)">
                    <input type="number" min="0" value={form.consumer_ticket_price} onChange={(e) => setForm({ ...form, consumer_ticket_price: e.target.value })} placeholder="Free" className="form-input" data-testid="event-ticket-price" />
                  </Field>
                </div>
                <Field label="Vendor signup URL" hint="Where vendors apply to vend">
                  <input value={form.signup_url} onChange={(e) => setForm({ ...form, signup_url: e.target.value })} placeholder="https://…" className="form-input" data-testid="event-signup-url" />
                </Field>
              </>
            )}
            <Field label="Consumer ticket / RSVP URL">
              <input value={form.consumer_signup_url} onChange={(e) => setForm({ ...form, consumer_signup_url: e.target.value })} placeholder="https://…" className="form-input" data-testid="event-consumer-signup-url" />
            </Field>
          </div>

          {/* Contact + tags */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Contact email">
              <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="info@event.com" className="form-input" data-testid="event-contact-email" />
            </Field>
            <Field label="Contact phone">
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+1 …" className="form-input" data-testid="event-contact-phone" />
            </Field>
          </div>
          <Field label="Organizer website">
            <input value={form.organizer_website} onChange={(e) => setForm({ ...form, organizer_website: e.target.value })} placeholder="https://…" className="form-input" data-testid="event-organizer-website" />
          </Field>
          <Field label="Tags (comma separated)" hint="e.g. market, native-art, festival">
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="market, festival" className="form-input" data-testid="event-tags" />
          </Field>

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting || !!dupWarning}
              className="w-full sm:w-auto px-6 py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white font-bold flex items-center justify-center gap-2 shadow-[0_8px_24px_-8px_rgba(168,85,247,0.7)] disabled:opacity-50"
              data-testid="event-submit"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Posting…' : 'Post Event'}
              <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, hint, icon: Icon, required, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-300 flex items-center gap-1 mb-1.5">
      {Icon && <Icon size={11} className="text-fuchsia-300" />}
      {label}
      {required && <span className="text-fuchsia-400">*</span>}
    </span>
    {children}
    {hint && <span className="text-[11px] text-zinc-500 mt-1 block">{hint}</span>}
  </label>
);
